import type { ReactNode } from 'react';

import { Text } from '@evisort/eds/components/Text/Text';
import { defineMessages, useIntl } from 'react-intl';

import brandLockupSrc from '~/assets/images/brand/brand-color-blue-horizontal.svg';
import brandLockupDarkSrc from '~/assets/images/brand/brand-color-white-horizontal.svg';
import { TimeOfDayGradientBackground } from '~/common/brandGradient/TimeOfDayGradientBackground';
import { useTimeOfDayGradient } from '~/common/brandGradient/useTimeOfDayGradient';
import { BRAND_NAME } from '~/common/constants/brand';

import styles from './AuthPageLayout.module.css';

const messages = defineMessages({
  copyright: {
    id: 'AuthPageLayout.copyright',
    defaultMessage: '© {year} Workday, Inc. All rights reserved.',
    description: 'Footer copyright notice on unauthenticated pages',
  },
  logoAlt: {
    id: 'AuthPageLayout.logoAlt',
    defaultMessage: '{brandName} logo',
    description: 'Alt text for the Evisort brand lockup',
  },
  logoLinkTitle: {
    id: 'AuthPageLayout.logoLinkTitle',
    defaultMessage: '{brandName} login page',
    description: 'Title for the logo link on unauthenticated pages',
  },
});

interface Props {
  children: ReactNode;
  /** Left-column marketing content. Hidden below the split-screen breakpoint. */
  marketing?: ReactNode;
  /**
   * Condensed `marketing` (headline + tagline, no feature list), rendered
   * above the card instead of inside it once the breakpoint hides `marketing`.
   */
  marketingHeader?: ReactNode;
}

function BrandLockup({ className }: { className?: string }) {
  const intl = useIntl();
  const alt = intl.formatMessage(messages.logoAlt, { brandName: BRAND_NAME });

  return (
    <a
      className={className}
      href="/"
      title={intl.formatMessage(messages.logoLinkTitle, {
        brandName: BRAND_NAME,
      })}
    >
      {/* CSS shows whichever variant fits `data-time-of-day` — Color_Blue
          loses contrast against the Night gradient's dark end. */}
      <img alt={alt} className={styles.logoLight} src={brandLockupSrc} />
      <img alt={alt} className={styles.logoDark} src={brandLockupDarkSrc} />
    </a>
  );
}

/**
 * Shared chrome for unauthenticated pages: brand lockup and footer over a
 * split-screen gradient, `marketing` on the left and the form card on the
 * right.
 */
export function AuthPageLayout({
  children,
  marketing,
  marketingHeader,
}: Props) {
  const intl = useIntl();
  const { period } = useTimeOfDayGradient();

  return (
    <div className={styles.page} data-time-of-day={period}>
      <TimeOfDayGradientBackground period={period} />

      <div className={styles.pageInner}>
        <BrandLockup className={styles.logoLink} />

        <div className={styles.body}>
          {marketing && <div className={styles.marketing}>{marketing}</div>}
          {marketingHeader && (
            <div className={styles.marketingHeaderMobile}>
              {marketingHeader}
            </div>
          )}
          <div className={styles.cardColumn}>
            <div className={styles.surface}>{children}</div>
          </div>
        </div>

        <div className={styles.footer}>
          <Text as="p" variant="body-s">
            {intl.formatMessage(messages.copyright, {
              year: new Date().getFullYear(),
            })}
          </Text>
        </div>
      </div>
    </div>
  );
}
